import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { useEagerAudioInit } from '@/hooks/useAudioContext';
import { SmallScreenGuard } from '@/components/SmallScreenGuard/SmallScreenGuard';
import { LoadingView } from '@/components/LoadingView';
import { ToolLayout } from '@/pages/ToolLayout';
import { Welcome } from '@/pages/Welcome';
import { Generation } from '@/pages/Generation';
import { UploadSummary } from '@/pages/UploadSummary';
import { ThankYou } from '@/pages/ThankYou';
import { NotFound } from '@/pages/NotFound';

// Der Admin-Zweig liegt hinter Forward Auth (Authentik) und darf NICHT im oeffentlichen
// Bundle landen: Talent-Routen (/:id) sind ohne Login erreichbar, und ein
// gemeinsames Bundle liefert den Admin-Code an jeden Besucher mit aus — im
// Inspector lesbar. Kein Datenleck (Secrets sind serverseitig, /admin/* haengt
// hinter Forward Auth + JWT-Gate), aber Komponenten-, Routen- und API-Pfadnamen sind
// Aufklaerungsmaterial. Route-Level-Split: dieser Chunk wird erst nach dem
// Authentik-Login angefordert.
// Siehe 00_TRO_DEV_DOCS/security-checklist.md Teil 2 und public-frontend-check.md.
const AdminLayout = lazy(() =>
  import('@/pages/admin/AdminLayout').then((m) => ({ default: m.AdminLayout }))
);
const AdminDashboard = lazy(() =>
  import('@/pages/admin/Dashboard').then((m) => ({ default: m.AdminDashboard }))
);
const ToolDetail = lazy(() =>
  import('@/pages/admin/ToolDetail').then((m) => ({ default: m.ToolDetail }))
);
const ToolEditRoute = lazy(() =>
  import('@/pages/admin/ToolEditRoute').then((m) => ({ default: m.ToolEditRoute }))
);
const ToolEditExistingRoute = lazy(() =>
  import('@/pages/admin/ToolEditExistingRoute').then((m) => ({
    default: m.ToolEditExistingRoute,
  }))
);

export default function App() {
  useEagerAudioInit();

  return (
    <>
      {/* Nur die Admin-Routen sind lazy — der Fallback erscheint also
          ausschliesslich dort. LoadingView selbst bleibt im Hauptbundle
          (nutzt AppShell, das die Talent-Seiten ohnehin laden). */}
      <Suspense fallback={<LoadingView message="Loading…" />}>
        <Routes>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="project/new" element={<ToolEditRoute />} />
            <Route path="project/:id" element={<ToolDetail />} />
            <Route path="project/:id/edit" element={<ToolEditExistingRoute />} />
          </Route>
          <Route path="/:id" element={<ToolLayout />}>
            <Route index element={<Welcome />} />
            <Route path="generate" element={<Generation />} />
            <Route path="summary" element={<UploadSummary />} />
            <Route path="done" element={<ThankYou />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      <SmallScreenGuard />
    </>
  );
}
